import React from 'react';
import { defineMessages, t } from '@sm/intl';
import { createURL } from '@sm/utils';

import EndpageHeader from '~app/components/EndpageHeader';
import SimpleHero from '~app/components/SimpleHero';
import FortuneCallout from '~app/components/FortuneCallout';
import ProductsCalloutList, { ProductsCalloutListProps } from '~app/components/ProductsCalloutList';

import AudienceIconGreen from '~static/images/icons/audience-icon-green.svg';
import EnterpriseIconBlue from '~static/images/icons/enterprise-icon-blue.svg';
import { getSrc } from '~app/helpers/assetLoading';

const COPY = defineMessages({
  ENDPAGE_HEADER_COPY: {
    id: 'SurveyThanks.RebrandExistingUser.EndpageHeaderCopy',
    defaultMessage: 'Thank you for taking this survey.',
    description: '[Type: Header][Vis: med] - Informs the viewer that this survey has just completed',
  },

  PRODUCT_CALLOUT_AUDIENCE_HEADING: {
    id: 'SurveyThanks.RebrandExistingUser.ProductCalloutAudienceHeader',
    defaultMessage: 'Survey your target market',
    description:
      '[Type: Header][Vis: med] - Tagline to help entice users to views click the cta below. The CTA leads to our Market Research, Audience - marketing pages',
  },
  PRODUCT_CALLOUT_AUDIENCE_BODY: {
    id: 'SurveyThanks.RebrandExistingUser.ProductCalloutAudienceBody',
    defaultMessage: 'SurveyMonkey Audience has millions of people ready to take your survey.',
    description: '[Type: Paragraph][Vis: med] - Gives context to the above Tagline',
  },
  PRODUCT_CALLOUT_AUDIENCE_CTA: {
    id: 'SurveyThanks.RebrandExistingUser.ProductCalloutAudienceCTA',
    defaultMessage: 'Learn more',
    description: '[Type: Button][Vis: med] - The text for a button that leads users to an informational marketing page',
  },

  PRODUCT_CALLOUT_ENTERPRISE_HEADING: {
    id: 'SurveyThanks.RebrandExistingUser.ProductCalloutEnterpriseHeader',
    defaultMessage: 'Want more power and multiple users?',
    description: "[Type: Header][Vis: med] - Tag line introducing SurveyMonkey's Enterprise plan's value proposition",
  },
  PRODUCT_CALLOUT_ENTERPRISE_BODY: {
    id: 'SurveyThanks.RebrandExistingUser.ProductCalloutEnterpriseBody',
    defaultMessage: 'Get our most powerful features and make smarter decisions with SurveyMonkey Enterprise.',
    description:
      '[Type: Paragraph][Vis: med] - Expands and reiterate the value proposition for the SurveyMonkey Enterprise plan',
  },

  FORTUNE_CALLOUT_COPY: {
    id: 'SurveyThanks.RebrandExistingUser.FortuneCalloutCopy',
    defaultMessage: '95% of the Fortune 500 rely on SurveyMonkey for People Powered Data',
    description: '[Type: Header][Vis: low] - A promotional statistic designed to show corporate trust and prestige',
  },
  FORTUNE_CALLOUT_CTA: {
    id: 'SurveyThanks.RebrandExistingUser.FortuneCalloutCTA',
    defaultMessage: 'Create survey',
    description: "[Type: Button][Vis: high] - Fortune's CTA which encourages our users to create a survey",
  },
});

type ItemProps = ProductsCalloutListProps['calloutItem1'];

function RebrandExistingUser(): React.ReactElement {
  const utSource = 'survey-thanks';
  const utSource2 = 'RebrandExistingUser';
  const utSource3 = 'ProductCalloutItem';

  const CALLOUT_ITEM_AUDIENCE: ItemProps = {
    icon: getSrc(AudienceIconGreen),
    heading: t(COPY.PRODUCT_CALLOUT_AUDIENCE_HEADING),
    copy: t(COPY.PRODUCT_CALLOUT_AUDIENCE_BODY),
    href: createURL('/market-research/solutions/audience-panel/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: t(COPY.PRODUCT_CALLOUT_AUDIENCE_CTA),
    }),
    ctaText: COPY.PRODUCT_CALLOUT_AUDIENCE_CTA,
    ctaUsage: 'market research cta',
    ctaColor: 'classic-sabaeus',
    name: 'audience',
    color: 'dark',
  };

  const CALLOUT_ITEM_ENTERPRISE: ItemProps = {
    icon: getSrc(EnterpriseIconBlue),
    heading: t(COPY.PRODUCT_CALLOUT_ENTERPRISE_HEADING),
    copy: t(COPY.PRODUCT_CALLOUT_ENTERPRISE_BODY),
    href: createURL('/mp/enterprise/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: t(COPY.PRODUCT_CALLOUT_AUDIENCE_CTA),
    }),
    ctaText: COPY.PRODUCT_CALLOUT_AUDIENCE_CTA,
    ctaColor: 'classic-midnight',
    ctaUsage: 'enterprise cta',
    name: 'enterprise',
    color: 'dark',
  };

  return (
    <>
      <EndpageHeader copy={t(COPY.ENDPAGE_HEADER_COPY)} hasLogo headerColor="sabaeus" headerType="thanks" />
      <SimpleHero hideCTAs={false} utSource={utSource} utSource2={utSource2} />
      <ProductsCalloutList
        calloutItem1={CALLOUT_ITEM_AUDIENCE}
        calloutItem2={CALLOUT_ITEM_ENTERPRISE}
        utSource={utSource}
      />
      <FortuneCallout
        copy={t(COPY.FORTUNE_CALLOUT_COPY)}
        ctaText={COPY.FORTUNE_CALLOUT_CTA}
        ctaUsage="sign-up tertiary"
        utSource={utSource}
        route="/create/start/"
        color="sabaeus"
        utSource2="RebrandExistingUser"
      />
    </>
  );
}

export default RebrandExistingUser;
